import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { TrlFieldAlertIcon } from '»components/atoms/TrlFieldAlertIcon';
import { TrlFormHelperText } from '»components/atoms/TrlFormHelperText';

import { TrlFieldContainer } from '../TrlFieldContainer';
import { TrlLockedField } from '../TrlLockedField';
import {
  BootstrapInputStyled,
  FontAwesomeIconStyled,
  FormControlStyled,
  IconButtonStyled,
} from './TrlTextField.styles';

export const TrlTextField = ({
  className,
  name,
  label,
  type,
  margin,
  checkTouched = true,
  disabled,
  placeholder,
  warningText,
  isHidden,
  isRequired,
  extraInfo,
  additionalFunc,
  autoComplete,
  hasPadding,
  isLocked,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <Field name={name}>
      {({ field, meta, form }) => {
        const getHelperText = () => {
          if (warningText) return warningText;
          if (checkTouched) return (meta.touched && meta.error) ?? null;
          return meta.error;
        };

        const getError = () => {
          if (checkTouched) return !!(meta.touched && meta.error);
          return !!meta.error;
        };

        const getEndAdornment = () => {
          if (type === 'password') {
            return (
              <IconButtonStyled onClick={handleClickShowPassword} edge="end">
                <FontAwesomeIconStyled
                  icon={showPassword ? faEyeSlash : faEye}
                />
              </IconButtonStyled>
            );
          }

          if (checkTouched) {
            if (meta.touched)
              return (
                <TrlFieldAlertIcon showIcon={!!meta.error} alertType="error" />
              );

            return null;
          }

          return (
            <TrlFieldAlertIcon showIcon={!!meta.error} alertType="error" />
          );
        };

        return (
          <FormControlStyled
            className={className}
            margin={margin}
            variant="standard"
            isHidden={isHidden}
          >
            <TrlFieldContainer
              label={label}
              isRequired={isRequired && !disabled && !isLocked}
              extraInfo={extraInfo}
              noPadding={!hasPadding}
              disabled={disabled}
            >
              <>
                {isLocked ? (
                  <TrlLockedField
                    showAlertIcon={!!warningText}
                    value={field.value}
                  />
                ) : (
                  <BootstrapInputStyled
                    name={name}
                    {...(type === 'password'
                      ? {
                          type: showPassword ? 'text' : 'password',
                        }
                      : { type })}
                    disabled={disabled}
                    {...form}
                    {...meta}
                    {...field}
                    {...rest}
                    onChange={(e) => {
                      field.onChange(e);
                      if (additionalFunc) additionalFunc(e);
                    }}
                    autoComplete={autoComplete || 'off'}
                    placeholder={placeholder}
                    endAdornment={getEndAdornment()}
                    error={getError()}
                  />
                )}
                {(!!warningText ||
                  (checkTouched ? meta.touched && meta.error : meta.error)) && (
                  <TrlFormHelperText
                    helperType={
                      warningText ? 'warning' : meta.error ? 'error' : 'default'
                    }
                    helperText={getHelperText()}
                  />
                )}
              </>
            </TrlFieldContainer>
          </FormControlStyled>
        );
      }}
    </Field>
  );
};
TrlTextField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  type: PropTypes.string,
  margin: PropTypes.string,
  checkTouched: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  warningText: PropTypes.string,
  isHidden: PropTypes.bool,
  isRequired: PropTypes.bool,
  extraInfo: PropTypes.string,
  additionalFunc: PropTypes.func,
  autoComplete: PropTypes.string,
  hasPadding: PropTypes.bool,
  isLocked: PropTypes.bool,
};
